import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, Send, CheckCircle2 } from "lucide-react";

/**
 * Appeal panel for a user under an active QA state.
 * - user: current user object from AuthContext
 * - onDone(): callback after submit
 */
export default function AppealPanel({ user, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(!!user?.qa_appeal);

  const APPEALABLE = new Set(["under_review", "warned", "training_required", "improvements_required", "suspended"]);
  if (!user || !APPEALABLE.has(user.qa_status)) return null;

  const submit = async () => {
    if (text.trim().length < 20) return toast.error("Please provide at least 20 characters of context.");
    setBusy(true);
    try {
      await api.post("/qa/my-appeal", { text });
      toast.success("Appeal submitted — an admin will review it shortly.");
      setSubmitted(true);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="appeal-panel">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-[#0A0A0A] flex items-center justify-center flex-shrink-0 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-outfit font-bold">Under Admin Review</div>
          <div className="font-outfit font-semibold text-white mt-1">
            Your account is currently: {(user.qa_status || "").replace(/_/g, " ")}
          </div>
          <p className="text-sm text-white/85 font-manrope mt-1">
            You can submit one written appeal explaining your side. An admin will review your appeal alongside the reports before making a final decision.
          </p>

          {submitted || user.qa_appeal ? (
            <div className="mt-3 flex items-start gap-2 text-sm font-manrope text-gold-600">
              <CheckCircle2 className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">Appeal received</div>
                <div className="text-white/70 mt-1 whitespace-pre-wrap">
                  &ldquo;{((user.qa_appeal?.text || text) || "").slice(0, 400)}{((user.qa_appeal?.text || text) || "").length > 400 ? "…" : ""}&rdquo;
                </div>
                <div className="text-xs text-white/60 mt-1">Submitted {user.qa_appeal?.submitted_at ? new Date(user.qa_appeal.submitted_at).toLocaleString() : "just now"}</div>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <Label className="text-sm">Your written response</Label>
              <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)}
                        placeholder="Explain the context, any misunderstandings, and what you've done to address concerns." data-testid="appeal-text" />
              <Button onClick={submit} disabled={busy} className="mt-3 bg-brand hover:bg-brand-hover text-white" data-testid="appeal-submit-btn">
                <Send className="w-4 h-4 mr-2" /> {busy ? "Submitting…" : "Submit Appeal"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
